import { ID, Task, TaskType, Timestamp } from '@griply/types';
import { canOpenHabitOptions } from '@griply/utils';
import React, { forwardRef, useMemo, useState } from 'react';
import { TaskCalendarSyncList } from 'shared/components/connected/task-calendar-sync-list';
import {
  DateSubMenu,
  DeadlineDeadlineSubMenu,
  LinkedItemSubMenu,
  PrioritySubMenu,
  RemindersSubMenu,
  TagsSubMenu,
} from 'shared/components/connected/task-card-multi-select-context-menu';
import { TimeslotContextMenu } from 'shared/components/connected/timeslot-context-menu';
import { DatePresets } from 'shared/components/ui/datepicker-with-options';
import { Placement, PopupMenu } from 'shared/components/ui/popup-menu';
import { OverdueContextMenu } from 'shared/components/ui/task-detail-checkbox/overdue-context-menu';
import { useToday } from 'shared/contexts/today';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { InteractionType } from 'shared/types/data-type';

import { CompleteList } from './complete-list';
import { EditList } from './edit-list';
import { LifecycleList } from './lifecycle-list';
import { RestoreList } from './restore-list';
import { OverdueEntryPrompt, SubMenu } from './types';

const emptyDatePresets: DatePresets[] = [];

export type TaskCardContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'onOutside'
> & {
  task: Task;
  onClose: () => void;
  nameRef?: React.RefObject<HTMLInputElement>;
  hideMeta?: boolean;
  hideEdit?: boolean;
  occurrenceId?: ID;
  timeslotDate?: Timestamp;
  initialActiveSubMenu?: SubMenu;
  onAction?: () => void;
  onUpdate?: (task: Task, updates: any) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  showCalendarSync?: boolean;
};

export const TaskCardContextMenu = forwardRef<
  HTMLDivElement,
  TaskCardContextMenuProps
>(
  (
    {
      task,
      onClose,
      referenceElement,
      location,
      nameRef,
      hideEdit,
      occurrenceId,
      timeslotDate,
      initialActiveSubMenu,
      onAction,
      onUpdate,
      onDelete,
      onDuplicate,
      showCalendarSync,
    },
    ref,
  ) => {
    const [openSubMenu, setOpenSubMenu] = useState<SubMenu | undefined>(
      initialActiveSubMenu,
    );
    const [overduePrompt, setOverduePrompt] = useState<OverdueEntryPrompt>();
    const today = useToday();

    const isSubtask = !!task.parentIds?.length;
    const isHabit = [TaskType.Habit, TaskType.Repeating].includes(task.type);
    const isFutureOccurrence = useMemo(
      () =>
        !!timeslotDate &&
        !canOpenHabitOptions(task, { today, displayDate: timeslotDate }),
      [task, timeslotDate, today],
    );

    useKeysDown(
      ['KeyP'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.Priority);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['KeyR'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.ReminderTime);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['KeyG'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.LinkedItem);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['KeyD'],
      (e) => {
        if (e.shiftKey) {
          e.preventDefault();
          setOpenSubMenu(SubMenu.DeadlineDeadline);
          return;
        }
        if (e.metaKey || e.ctrlKey || e.altKey) {
          return;
        }
        e.preventDefault();
        setOpenSubMenu(SubMenu.Date);
      },
      { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
    );

    useKeysDown(
      ['KeyT'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.Tags);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['Escape'],
      (e) => {
        e.stopImmediatePropagation();
        onClose();
      },
      { ignoreInputFields: true },
    );

    const menuProps = {
      ref,
      location,
      referenceElement,
      onOutside: onClose,
      interactionType: InteractionType.Task,
      position: 'bottom-start' as Placement,
    };

    return (
      <>
        {!openSubMenu && !overduePrompt && (
          <PopupMenu {...menuProps}>
            {isSubtask && (
              <LifecycleList
                task={task}
                onClose={onClose}
                onAction={onAction}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                occurrenceDate={timeslotDate}
              />
            )}
            {!isSubtask &&
              (isHabit && isFutureOccurrence && !task.deletedAt ? (
                <>
                  <EditList
                    task={task}
                    onClose={onClose}
                    nameRef={nameRef}
                    openSubMenu={setOpenSubMenu}
                    onAction={onAction}
                    onUpdate={onUpdate}
                    hideEdit={hideEdit}
                    occurrenceDate={timeslotDate}
                  />
                  <LifecycleList
                    task={task}
                    onClose={onClose}
                    onAction={onAction}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                    occurrenceDate={timeslotDate}
                  />
                </>
              ) : (
                <>
                  {task.deletedAt && (
                    <RestoreList task={task} onClose={onClose} />
                  )}
                  <EditList
                    task={task}
                    onClose={onClose}
                    nameRef={nameRef}
                    openSubMenu={setOpenSubMenu}
                    onAction={onAction}
                    onUpdate={onUpdate}
                    hideEdit={hideEdit}
                    occurrenceDate={timeslotDate}
                    completeActions={
                      isHabit ? (
                        <CompleteList
                          task={task}
                          onClose={onClose}
                          date={timeslotDate}
                          onOverdue={setOverduePrompt}
                        />
                      ) : undefined
                    }
                  />
                  {showCalendarSync && (
                    <TaskCalendarSyncList
                      task={task}
                      onClose={onClose}
                      onAction={onAction}
                    />
                  )}
                  {!task.deletedAt && (
                    <LifecycleList
                      task={task}
                      onClose={onClose}
                      onAction={onAction}
                      onDelete={onDelete}
                      onDuplicate={onDuplicate}
                      occurrenceDate={timeslotDate}
                    />
                  )}
                  {/*{isHabit && <HabitList onClose={onClose} task={task} />}*/}
                </>
              ))}
          </PopupMenu>
        )}

        {!!overduePrompt && (
          <OverdueContextMenu
            location={location}
            referenceElement={referenceElement}
            position="bottom-start"
            interactionType={InteractionType.Task}
            name={task.name}
            overdueDate={overduePrompt.overdueDate}
            type={overduePrompt.type}
            onDate={overduePrompt.onDate}
            onClose={onClose}
          />
        )}

        {openSubMenu === SubMenu.Priority && (
          <PrioritySubMenu
            tasks={[task]}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.LinkedItem && (
          <LinkedItemSubMenu
            tasks={[task]}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.DeadlineDeadline && (
          <DeadlineDeadlineSubMenu
            tasks={[task]}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.ReminderTime && (
          <RemindersSubMenu
            tasks={[task]}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.Date && (
          <DateSubMenu
            tasks={[task]}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.SelectDate && (
          <DateSubMenu
            tasks={[task]}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
            datePresets={emptyDatePresets}
            canRemoveValue={false}
            occurrenceDate={isHabit ? timeslotDate : undefined}
          />
        )}

        {openSubMenu === SubMenu.Tags && (
          <TagsSubMenu tasks={[task]} onClose={onClose} {...menuProps} />
        )}

        {openSubMenu === SubMenu.Timeslot && (
          <TimeslotContextMenu
            {...menuProps}
            task={task}
            onClose={onClose}
            representedDay={timeslotDate!}
          />
        )}
      </>
    );
  },
);
